import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_MEALS_LIST } from 'graphql/common/queries'
import { ADD_MEAL, UPDATE_MEAL } from 'graphql/common/mutations'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'

const MAX_MEAL_AMOUNT = 9999999.99

export default function MealForm() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [mealInfo, setMealInfo] = useState(null)

    const blockInvalidNumberChars = e => {
        if (['e', 'E', '+', '-'].includes(e.key)) {
            e.preventDefault()
        }
    }

    const handleAmountChange = e => {
        const { name, value } = e.target

        if (value === '') {
            CreateMealFormValidation.setFieldValue(name, '')
            return
        }

        const normalized = value.replace(',', '.')

        if (!/^\d*\.?\d{0,2}$/.test(normalized)) {
            return
        }

        const parsed = Number(normalized)

        if (!Number.isFinite(parsed)) {
            return
        }

        if (parsed > MAX_MEAL_AMOUNT) {
            CreateMealFormValidation.setFieldValue(name, MAX_MEAL_AMOUNT)
            return
        }

        CreateMealFormValidation.setFieldValue(name, normalized)
    }

    const handleBack = () => {
        navigate('/meals')
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setShowErrDialog(false)
    }

    const [getMealById, { loading: mealLoading, error: mealError }] = useLazyQuery(GET_MEALS_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.searchMeal.success && data.searchMeal.code === '200') {
                setMealInfo(data.searchMeal.meals[0])
            }
        },
    })

    useEffect(() => {
        if (id) {
            getMealById({
                variables: {
                    page: {
                        limit: 1,
                        offset: 0,
                    },
                    searchMealId: id,
                },
            })
        }
    }, [id])

    const [addNewMeal, { loading: addLoading }] = useMutation(ADD_MEAL, {
        onCompleted: data => {
            console.log('Add meal response:', data)

            if (data?.addMeal?.code === '200' && data?.addMeal?.success === true) {
                CreateMealFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateMealFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setErrorMessage(data?.addMeal?.message || 'An error occurred while creating the meal')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            console.error('Add meal error:', error)
            setSubmitLoading(false)
            CreateMealFormValidation.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const [updateMeal, { loading: updateLoading }] = useMutation(UPDATE_MEAL, {
        onCompleted: data => {
            console.log('Update meal response:', data)

            if (data?.updateMeal?.code === '200' && data?.updateMeal?.success === true) {
                CreateMealFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setShowSucDialog(true)
                setTimeout(() => {
                    handleBack()
                }, 500)
            } else {
                CreateMealFormValidation.setSubmitting(false)
                setSubmitLoading(false)
                setErrorMessage(data?.updateMeal?.message || 'An error occurred while updating the meal')
                setShowErrDialog(true)
            }
        },
        onError: error => {
            console.error('Update meal error:', error)
            setSubmitLoading(false)
            CreateMealFormValidation.setSubmitting(false)
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const CreateMealFormValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            description_en: mealInfo?.description.en || '',
            description_ar: mealInfo?.description.ar || '',
            price_base: mealInfo?.price.base || '',
            price_tax: mealInfo?.price.tax || '',
            active: mealInfo?.active || false,
        },
        validationSchema: Yup.object().shape({
            description_en: Yup.string().required('English description is required'),
            description_ar: Yup.string().required('Arabic description is required'),
            price_base: Yup.number()
                .required('Base price is required')
                .min(0, 'Base price must be greater than or equal to 0')
                .max(MAX_MEAL_AMOUNT, `Base price must be less than or equal to ${MAX_MEAL_AMOUNT}`),
            price_tax: Yup.number()
                .required('Tax is required')
                .min(0, 'Tax must be greater than or equal to 0')
                .max(MAX_MEAL_AMOUNT, `Tax must be less than or equal to ${MAX_MEAL_AMOUNT}`),
        }),
        onSubmit: values => {
            setSubmitLoading(true)
            submitMeal(values)
        },
    })

    const submitMeal = values => {
        const mealData = {
            active: values.active,
            description: {
                en: values.description_en,
                ar: values.description_ar,
            },
            price: {
                tax: parseFloat(values.price_tax),
                base: parseFloat(values.price_base),
            },
        }

        if (id) {
            updateMeal({
                variables: {
                    mealId: id,
                    meal: mealData,
                },
            })
        } else {
            addNewMeal({
                variables: {
                    meal: mealData,
                },
            })
        }
        console.log('Meal data being sent:', { mealId: id, meal: mealData })
    }

    return (
        <>
            <div className="mb-8 flex justify-between items-center">
                <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateMealFormValidation.isSubmitting}
                        onClick={CreateMealFormValidation.handleSubmit}
                        type="button"
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </div>

            <form>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Meal Description')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t("Enter the meal's description in English and Arabic")}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="description_en"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('English Description')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="description_en"
                                        name="description_en"
                                        type="text"
                                        value={CreateMealFormValidation.values.description_en}
                                        onChange={CreateMealFormValidation.handleChange}
                                        onBlur={CreateMealFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateMealFormValidation.touched.description_en &&
                                        CreateMealFormValidation.errors.description_en && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateMealFormValidation.errors.description_en}
                                            </p>
                                        )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="description_ar"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Arabic Description')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="description_ar"
                                        name="description_ar"
                                        type="text"
                                        value={CreateMealFormValidation.values.description_ar}
                                        onChange={CreateMealFormValidation.handleChange}
                                        onBlur={CreateMealFormValidation.handleBlur}
                                        placeholder="Enter here"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateMealFormValidation.touched.description_ar &&
                                        CreateMealFormValidation.errors.description_ar && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateMealFormValidation.errors.description_ar}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                                {t('Price Information')}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Set the base price and tax amount for the meal')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="price_base"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Base Price')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="price_base"
                                        name="price_base"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={MAX_MEAL_AMOUNT}
                                        value={CreateMealFormValidation.values.price_base}
                                        onChange={handleAmountChange}
                                        onKeyDown={blockInvalidNumberChars}
                                        onBlur={CreateMealFormValidation.handleBlur}
                                        placeholder="0.00"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateMealFormValidation.touched.price_base &&
                                        CreateMealFormValidation.errors.price_base && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateMealFormValidation.errors.price_base}
                                            </p>
                                        )}
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="price_tax"
                                    className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                    {t('Tax')}
                                    <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="price_tax"
                                        name="price_tax"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={MAX_MEAL_AMOUNT}
                                        value={CreateMealFormValidation.values.price_tax}
                                        onChange={handleAmountChange}
                                        onKeyDown={blockInvalidNumberChars}
                                        onBlur={CreateMealFormValidation.handleBlur}
                                        placeholder="0.00"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    />
                                    {CreateMealFormValidation.touched.price_tax &&
                                        CreateMealFormValidation.errors.price_tax && (
                                            <p className="text-error text-xs mt-1">
                                                {CreateMealFormValidation.errors.price_tax}
                                            </p>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3">
                        <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">{t('Status')}</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {t('Set whether this meal is active or inactive')}
                            </p>
                        </div>

                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                            <div className="sm:col-span-6">
                                <div className="flex items-center">
                                    <input
                                        id="active"
                                        name="active"
                                        type="checkbox"
                                        checked={CreateMealFormValidation.values.active}
                                        onChange={CreateMealFormValidation.handleChange}
                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                    />
                                    <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                                        {t('Active')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-3">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Cancel')}
                    </button>
                    <button
                        disabled={CreateMealFormValidation.isSubmitting}
                        type="submit"
                        onClick={CreateMealFormValidation.handleSubmit}
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Save')}
                    </button>
                </div>
            </form>

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={t('Saved Successfully')}
                    onClose={handleDialogClose}
                />
            )}

            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
